import type { Kit, Permission } from '@cheqroom/api';
import { useMemo } from 'react';

import { createCheckoutPermissions } from './use-checkout-permissions.hook';
import { type Props as PermissionsProps, usePermissions } from './use-permissions.hook';
import { createReservationPermissions } from './use-reservation-permissions.hook';
import { canGiveItemCustody, canReleaseItemCustody, canTakeItemCustody, canUpdateItemGeo } from './utils/item';
import { canAdminKit } from './utils/kit';

export const createKitPermissions = (permissions: ReturnType<typeof usePermissions>) => {
	const { hasAnyPermission: _hasAnyPermission, useKits, useImport, useExport } = permissions;

	const { canCheckout } = createCheckoutPermissions(permissions);
	const { canReserve } = createReservationPermissions(permissions);

	const hasAnyPermission = (...required: Permission[]) => useKits && _hasAnyPermission(...required);

	const canAdmin = () => canAdminKit(permissions);

	return {
		canCreate: canAdmin,
		canUpdate: canAdmin,
		canRead: () => hasAnyPermission('KITS_READER', 'KITS_READER_RESTRICTED'),
		canDelete: canAdmin,
		canAddAttachment: () => hasAnyPermission('KITS_ATTACHMENTS_OWN_WRITER'),
		canRemoveAttachment: (ownAttachment?: boolean) =>
			hasAnyPermission('KITS_ATTACHMENTS_DELETER') ||
			(ownAttachment && hasAnyPermission('KITS_ATTACHMENTS_OWN_DELETER')),
		canAddComment: () => hasAnyPermission('KITS_COMMENTS_OWN_WRITER'),
		canEditComment: (own: boolean) => own && hasAnyPermission('KITS_COMMENTS_OWN_WRITER'),
		canRemoveComment: (ownComment?: boolean) =>
			hasAnyPermission('KITS_COMMENTS_DELETER') || (ownComment && hasAnyPermission('KITS_COMMENTS_OWN_DELETER')),
		canUpdatePermissions: canAdmin,
		canImport: () => useImport && hasAnyPermission('KITS_IMPORTER', 'KITS_IMPORTER_RESTRICTED'),
		canExport: () => useExport && hasAnyPermission('KITS_EXPORTER', 'KITS_EXPORTER_RESTRICTED'),
		canTakeCustody: (kit?: Pick<Kit, 'canCustody' | 'status'>) => {
			if (!canTakeItemCustody(permissions)) return false;
			return kit ? kit.canCustody === 'available' && kit.status === 'available' : true;
		},
		canGiveCustody: (kit?: Pick<Kit, 'canCustody'>) => {
			if (!canGiveItemCustody(permissions)) return false;
			return kit ? kit.canCustody === 'available' : true;
		},
		canReleaseCustody: (kit?: Pick<Kit, 'status'>) => {
			if (!canReleaseItemCustody(permissions)) return false;
			return kit ? kit.status === 'in_custody' : true;
		},
		canCheckout: (kit?: Pick<Kit, 'canOrder'>) => {
			if (!canCheckout(true)) return false;
			return kit ? kit.canOrder === 'available' || kit.canOrder === 'available_partially' : true;
		},
		canReserve: (kit?: Pick<Kit, 'canReserve'>) => {
			if (!canReserve(true)) return false;
			return kit ? kit.canReserve === 'available' || kit.canReserve === 'available_partially' : true;
		},
		canDuplicate: canAdmin,
		canChangeCover: canAdmin,
		canUpdateGeo: () => canUpdateItemGeo(permissions),
		canSyncReservations: (kit?: Pick<Kit, 'locked'>) => {
			if (!canAdmin()) return false;
			return kit ? kit.locked === true : false;
		},
	};
};

export const useKitPermissions = (props: PermissionsProps) => {
	const permissions = usePermissions(props);

	return useMemo(() => createKitPermissions(permissions), [permissions]);
};
